import React, { useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth, defaultRouteFor } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import LogoBrand from "../components/LogoBrand";
import LanguageSwitch from "../components/LanguageSwitch";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { toast } from "sonner";

export default function Login() {
  const { user, login, error } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [clock, setClock] = useState(Date.now());

  useEffect(() => {
    if (error?.code !== "account_locked") {
      return undefined;
    }
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [error]);

  const lockMessage = () => {
    if (error?.code !== "account_locked") {
      return error;
    }
    const expiry = new Date(error.locked_until).getTime();
    const remaining = Math.max(0, Math.ceil((expiry - clock) / 1000));
    const minutes = Math.floor(remaining / 60);
    const seconds = String(remaining % 60).padStart(2, "0");
    const localTime = new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(new Date(error.locked_until));
    return `Account temporarily locked after repeated failed sign-in attempts. `
      + `Try again in ${minutes}:${seconds} (${localTime}) or contact an administrator.`;
  };

  if (user) return <Navigate to={user.must_change_password ? "/change-password" : defaultRouteFor(user.role)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) {
      toast.success(t("auth.welcome"));
      // Route based on latest user from context — read via defaultRouteFor after login sets state
      // We can't read user here immediately (React batching); rely on the Navigate above on re-render.
    } else {
      toast.error(t("auth.invalid"));
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative" style={{
        backgroundImage: "url(https://images.unsplash.com/photo-1629140727571-9b5c6f6267b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob3RlbCUyMHJvb20lMjBjbGVhbnxlbnwwfHx8fDE3ODU5MTcwMDd8MA&ixlib=rb-4.1.0&q=85)",
        backgroundSize: "cover", backgroundPosition: "center",
      }}>
        <div className="absolute inset-0 bg-slate-900/40" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="font-serif text-4xl leading-tight">{t("tagline")}</div>
          <div className="mt-3 text-sm text-slate-200 max-w-md">{t("tagline_sub")}</div>
        </div>
      </div>
      <div className="flex flex-col items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="flex justify-end mb-4"><div className="[&_button]:!bg-white [&_button]:!text-slate-900 [&_button]:!border-slate-300"><LanguageSwitch variant="light" /></div></div>
          <div className="flex flex-col items-center mb-8">
            <LogoBrand variant="login" />
          </div>
          <Card className="p-6 md:p-8 border-slate-200 shadow-sm" data-testid="login-card">
            <h1 className="font-serif text-2xl text-slate-900 mb-1">{t("auth.sign_in")}</h1>
            <p className="text-sm text-slate-500 mb-6">{t("auth.enter_credentials")}</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email">{t("auth.email")}</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="h-11 mt-1" data-testid="login-email" placeholder="name@varunainn.com" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t("auth.password")}</Label>
                  <Link to="/forgot-password" className="text-xs text-slate-500 hover:text-slate-900" data-testid="forgot-password-link">
                    {t("auth.forgot_password")}
                  </Link>
                </div>
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="h-11 mt-1" data-testid="login-password" />
              </div>
              {error && <div className="text-sm text-red-600" data-testid="login-error">{lockMessage()}</div>}
              <Button type="submit" disabled={busy} className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white" data-testid="login-submit">
                {busy ? t("auth.signing_in") : t("auth.sign_in")}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
